// Pure helpers for the admin "Delete / Pause / Restore account" lifecycle.
//
// Soft-delete model: profiles rows are NEVER hard-deleted and auth.users are
// never touched. A deleted account is profiles.status='deleted' plus
// approved=false, rejected=true (the login gate blocks on either signal) and
// the audit columns deleted_at / deletion_reason / suspended_by. A paused
// account is the same with status='paused' and paused_at / paused_reason.
//
// Everything here is side-effect free so it can be unit-tested with plain node.

export const LIFECYCLE_KEYS = [
  "deletion_reason", "deleted_at", "paused_reason", "paused_at", "suspended_by", "deleted_prior_state",
];

/** True when a row carries any lifecycle audit value written by the Delete / Pause flow. */
export function hasAuditValue(p) {
  const src = p || {};
  return ["deleted_at", "paused_at", "deletion_reason", "paused_reason", "suspended_by"].some(k => !!src[k]);
}

/** Legacy "stripped" shape: the OLD pause/delete wrote approved=false, rejected=true but the
 *  status text column was stripped by updateProfile's column-fallback. Same boolean shape as a
 *  plain rejected applicant, so on its own it is NOT enough to call the account paused/deleted. */
export function isLegacyStrippedRow(p) {
  const s = normStatus(p && p.status);
  return !!p && p.rejected === true && p.approved !== true && s !== "paused" && s !== "deleted";
}

/** Trimmed, lower-cased account status text. The ONE normaliser used by the login gates and
 *  deriveAccountStatus so "Paused" or " paused" can never show as paused in admin while the
 *  user can still sign in. */
export const normStatus = v => String(v == null ? "" : v).trim().toLowerCase();

/** True when a profiles.status value blocks sign-in (paused or deleted), case/space-insensitive. */
export function isBlockedStatus(v) {
  const s = normStatus(v);
  return s === "paused" || s === "deleted";
}

/** Status shown in the admin UI for a profiles row. profiles.status of
 *  'deleted' or 'paused' WINS; a legacy-stripped row that still carries a deleted_at /
 *  paused_at audit value is shown as deleted / paused so it stays in the archive and can be
 *  restored; otherwise the approved/rejected booleans decide exactly as before
 *  (rejected > approved > pending). */
export function deriveAccountStatus(p) {
  const s = normStatus(p && p.status);
  if (s === "deleted" || s === "paused") return s;
  if (isLegacyStrippedRow(p)) {
    if (p.deleted_at) return "deleted";
    if (p.paused_at) return "paused";
  }
  if (p && p.rejected) return "rejected";
  if (p && p.approved) return "approved";
  return "pending";
}

/** Lifecycle audit fields carried onto an admin account object. */
export function lifecycleFieldsFromProfile(p) {
  const src = p || {};
  return {
    deletion_reason: src.deletion_reason || null,
    deleted_at: src.deleted_at || null,
    paused_reason: src.paused_reason || null,
    paused_at: src.paused_at || null,
    suspended_by: src.suspended_by || null,
    deleted_prior_state: src.deleted_prior_state || null,
  };
}

/** Merge a per-browser localStorage override onto a Supabase-hydrated LIVE
 *  account. Supabase wins for status and every lifecycle field: an override
 *  can never resurrect or hide a deleted account. The only override status
 *  still honoured is the legacy "Supabase says pending but the admin already
 *  approved/rejected it locally" case, and never paused/deleted. */
export function mergeLiveOverride(account, override) {
  if (!override) return account;
  const { status: overrideStatus, ...rest } = override;
  const restSafe = { ...rest };
  LIFECYCLE_KEYS.forEach(k => { delete restSafe[k]; });
  delete restSafe.dbStatus;   // the database-derived baseline is never taken from a per-browser override
  const overrideOk = overrideStatus && overrideStatus !== "pending"
    && overrideStatus !== "paused" && overrideStatus !== "deleted";
  const finalStatus = (account.status === "pending" && overrideOk) ? overrideStatus : account.status;
  return { ...account, ...restSafe, status: finalStatus };
}

/** True when a Supabase error means a column / schema-cache entry is missing. */
export function isMissingColumnError(err) {
  if (!err) return false;
  const code = String(err.code || "");
  if (code === "42703" || code === "PGRST204") return true;
  const msg = String(err.message || err.details || err || "");
  // A bare "schema cache" message (transient PGRST002 outage) is NOT a missing column.
  return /column .* does not exist|could not find the '.*' column/i.test(msg);
}

/** True when a single-row UPDATE ... .select().single() matched nothing (PostgREST PGRST116),
 *  i.e. a concurrent change made the conditional write a no-op. */
export function isNoRowMatchedError(err) {
  if (!err) return false;
  if (String(err.code || "") === "PGRST116") return true;
  return /0 rows|no rows|multiple \(or no\) rows/i.test(String(err.message || err.details || ""));
}

/** Returns a problem string when the row returned by the soft-delete UPDATE
 *  is not really deleted, else null. */
export function verifyDeletedRow(row) {
  if (!row) return "no row returned";
  if (String(row.status || "").toLowerCase() !== "deleted") return "status is '" + row.status + "', expected 'deleted'";
  if (row.rejected !== true) return "rejected is not true";
  if (row.approved === true) return "approved is still true";
  return null;
}

/** Returns a problem string when the row returned by the pause UPDATE is not really paused. */
export function verifyPausedRow(row) {
  if (!row) return "no row returned";
  if (String(row.status || "").toLowerCase() !== "paused") return "status is '" + row.status + "', expected 'paused'";
  if (row.rejected !== true) return "rejected is not true";
  if (row.approved === true) return "approved is still true";
  return null;
}

/** Returns a problem string when the row returned by the restore UPDATE is not fully restored,
 *  else null. Three legal shapes: approved (approved=true, rejected=false, status approved),
 *  pending (approved=false, rejected=false, status pending) and rejected (approved=false,
 *  rejected=true, status rejected). `expected` ("approved" | "pending" | "rejected") pins the
 *  shape the caller meant to write; when omitted any of the three consistent shapes passes. */
export function verifyRestoredRow(row, expected) {
  if (!row) return "no row returned";
  const st = normStatus(row.status);
  let shape = null;
  if (row.approved === true && row.rejected !== true && st === "approved") shape = "approved";
  else if (row.approved !== true && row.rejected !== true && st === "pending") shape = "pending";
  else if (row.approved !== true && row.rejected === true && st === "rejected") shape = "rejected";
  if (!shape) {
    if (row.approved === true && row.rejected === true) return "approved and rejected are both true";
    return "unexpected restored shape (approved=" + row.approved + ", rejected=" + row.rejected + ", status='" + row.status + "')";
  }
  if (expected && shape !== expected) return "restored as " + shape + ", expected " + expected;
  const left = ["deletion_reason", "deleted_at", "paused_reason", "paused_at", "deleted_prior_state"].filter(k => row[k] != null);
  if (left.length) return "reason columns not cleared: " + left.join(", ");
  return null;
}

/** State an applicant row had before it was soft deleted, or null for approved / paused rows.
 *  "pending" = neither approved nor rejected; "rejected" = rejected and not approved. A row
 *  whose status text is already paused/deleted has no prior state. */
export function applicantPriorState(row) {
  if (!row) return null;
  if (isBlockedStatus(row.status)) return null;
  if (row.approved === true) return null;
  if (row.rejected === true) return "rejected";
  return "pending";
}

/** True when the fresh row does not have the deleted_prior_state column (migration 0030 not run).
 *  select('*') returns the key (even when null) exactly when the column exists. */
export function priorStateColumnMissing(row) {
  return !row || !Object.prototype.hasOwnProperty.call(row, "deleted_prior_state");
}

export const MISSING_PRIOR_STATE_HINT =
  "Deleting or restoring a pending or rejected applicant needs migration 0030 (supabase/migrations/0030_profiles_deleted_prior_state.sql). " +
  "Run it in the Supabase SQL editor (if it was already run, also run: NOTIFY pgrst, 'reload schema';), then try again. Nothing was changed.";

/** Equality filter spec [[column, value|null], ...] that pins a conditional UPDATE to the exact
 *  state of a freshly read row. null means "IS NULL" (NULL-safe: .eq(col, null) would never match).
 *  A concurrent change to any pinned column makes the UPDATE match no row. */
export function stateFilterSpec(row, cols = ["approved", "rejected", "status"]) {
  const src = row || {};
  return cols.map(c => [c, src[c] === undefined || src[c] === null ? null : src[c]]);
}

/** Column payload for a soft delete. Built only from primitives so it can never
 *  carry stale object state. `priorState` ("pending" | "rejected") is set only when an applicant
 *  is deleted; approved / paused deletes do NOT write deleted_prior_state (so they keep working
 *  when migration 0030 has not been run) unless `clearPriorState` is true (the fresh row proves
 *  the column exists), which nulls any leftover value. */
export function buildDeletePayload({ reason, actorEmail, nowIso, priorState, clearPriorState }) {
  const payload = {
    approved: false,
    rejected: true,
    status: "deleted",
    deletion_reason: String(reason || "").trim(),
    deleted_at: nowIso,
    suspended_by: actorEmail || null,
    paused_reason: null,
    paused_at: null,
  };
  if (priorState === "pending" || priorState === "rejected") payload.deleted_prior_state = priorState;
  else if (clearPriorState) payload.deleted_prior_state = null;
  return payload;
}

/** Column payload for a soft pause (login blocked, fully restorable). */
export function buildPausePayload({ reason, actorEmail, nowIso }) {
  return {
    approved: false,
    rejected: true,
    status: "paused",
    paused_reason: String(reason || "").trim(),
    paused_at: nowIso,
    suspended_by: actorEmail || null,
    deletion_reason: null,
    deleted_at: null,
  };
}

export const RESTORE_PAYLOAD = Object.freeze({
  approved: true,
  rejected: false,
  status: "approved",
  deletion_reason: null,
  deleted_at: null,
  paused_reason: null,
  paused_at: null,
  suspended_by: null,
});

const statusOf = row => normStatus(row && row.status);

/** Prior state recorded on a fresh row by an applicant delete: "pending" | "rejected" | null. */
export function recordedPriorState(row) {
  const v = normStatus(row && row.deleted_prior_state);
  return v === "pending" || v === "rejected" ? v : null;
}

/** Column payload for a restore of a FRESH row. A row whose deleted_prior_state is pending or
 *  rejected is returned to THAT state and is never approved; every other row gets the existing
 *  approved restore, exactly as before. deleted_prior_state is cleared only when the column exists
 *  on the row. This is the single place that decides the restored shape, so no restore path can
 *  produce approved=true for a row whose prior state is pending or rejected. */
export function buildRestorePayload(row) {
  const prior = recordedPriorState(row);
  const clear = row && Object.prototype.hasOwnProperty.call(row, "deleted_prior_state") ? { deleted_prior_state: null } : {};
  if (prior === "pending") return { ...RESTORE_PAYLOAD, approved: false, rejected: false, status: "pending", ...clear };
  if (prior === "rejected") return { ...RESTORE_PAYLOAD, approved: false, rejected: true, status: "rejected", ...clear };
  return { ...RESTORE_PAYLOAD, ...clear };
}

/** The shape buildRestorePayload will write: "approved" | "pending" | "rejected". */
export function restoredShapeFor(row) {
  return recordedPriorState(row) || "approved";
}

/** Fresh-row guard for Delete / Pause. Returns a human message when the action must be
 *  refused, else null. `action` is "delete" | "pause". Pause: approved accounts only. Delete:
 *  approved, paused, pending or rejected accounts (applicants are soft deleted to the same archive
 *  and restore to their previous state). Admin rows, the acting admin's own row, already deleted
 *  rows, contradictory rows and legacy rows that carry an old delete/pause record are refused. */
export function lifecycleBlockReason(row, { action, actorId, userId } = {}) {
  if (!row) return "The account could not be loaded.";
  const rowId = row.id != null ? row.id : userId;
  if (String(row.role || "").toLowerCase() === "admin") return "Admin accounts cannot be deleted or paused from here.";
  if (actorId && rowId != null && String(actorId) === String(rowId)) return "You cannot delete or pause your own account.";
  const s = statusOf(row);
  if (s === "deleted") return "This account is already deleted.";
  if (s === "paused") return action === "delete" ? null : "This account is already paused.";
  if (row.approved === true && row.rejected !== true) return null;   // approved
  if (action !== "delete") return "Only approved accounts can be paused.";
  if (row.approved === true) return "This account has contradictory approved / rejected values. Reload the page and check its current state.";
  if (hasAuditValue(row)) return "This account carries an earlier delete or pause record from the old flow. If it is listed in Deleted & paused accounts, use Restore there; otherwise it cannot be deleted from here (use Approve if this person should have access, or ask a developer).";
  return null;   // pending or rejected applicant
}

/** Guard for the Edit Account modal save. `fresh` is the profiles row re-read immediately before the
 *  write. `loadedStatus` is the DATABASE-derived status the account had when the modal was opened
 *  (never the per-browser override status: an override that merely differs from the database must
 *  not block a save). `openedStatus` is the status the modal showed when opened (may come from an
 *  override; defaults to loadedStatus) and `editedStatus` the status now selected.
 *  Refuses ONLY when (a) the fresh row is deleted or paused, or (b) the fresh database status differs
 *  from the database baseline (a genuine concurrent change), or (c) the fresh read failed.
 *  `writeStatus` is true only when the admin changed the status in THIS edit (edited !== opened) AND
 *  the fresh row still equals the database baseline; approved / rejected are never written otherwise.
 *  `reason` is "read-failed" | "gone" | "changed" on refusal; only "gone" should close the modal. */
export function editSaveGuard({ fresh, loadedStatus, openedStatus, editedStatus } = {}) {
  if (!fresh) return { ok: false, writeStatus: false, reason: "read-failed", message: "The account could not be re-checked before saving, so nothing was saved. Your edits are still in the window; check your connection and press Save again." };
  const cur = deriveAccountStatus(fresh);
  if (cur === "deleted" || cur === "paused") {
    return { ok: false, writeStatus: false, reason: "gone", message: "This account was " + cur + " by another admin while you were editing, so nothing was saved. Reload the page; Restore it from Deleted & paused accounts first if you still need to edit it." };
  }
  // No usable database baseline (account object without dbStatus and the open-time read failed):
  // fall back to the row we just read, so a status change the admin made is still written instead
  // of silently dropped. The fresh read happens immediately before the write.
  const loaded = normStatus(loadedStatus) || cur;
  if (loaded && cur !== loaded) {
    return { ok: false, writeStatus: false, reason: "changed", message: "Another admin changed this account's status to " + cur + " in the database (it was " + loaded + " when you opened it), so nothing was saved. Close this window and reopen the account to see its current state before editing again." };
  }
  const opened = normStatus(openedStatus != null && openedStatus !== "" ? openedStatus : loadedStatus);
  return { ok: true, writeStatus: !!loaded && normStatus(editedStatus) !== opened, reason: null, message: null };
}

/** UI gate for PAUSE: approved accounts only. */
export function canOfferLifecycle(account, currentUserId) {
  if (!account) return false;
  if (account.role === "admin") return false;
  if (currentUserId && (String(account.id) === String(currentUserId) ||
      String(account.supabaseId || "") === String(currentUserId))) return false;
  return account.status === "approved";
}

/** UI gate for DELETE: approved, pending or rejected accounts. */
export function canOfferDelete(account, currentUserId) {
  if (!account) return false;
  if (account.role === "admin") return false;
  if (currentUserId && (String(account.id) === String(currentUserId) ||
      String(account.supabaseId || "") === String(currentUserId))) return false;
  // A pending / rejected row that carries any earlier delete / pause audit value is refused by the
  // strict helper (Restore is the way back), so do not offer the button for it.
  if ((account.status === "pending" || account.status === "rejected") && hasAuditValue(account)) return false;
  return account.status === "approved" || account.status === "pending" || account.status === "rejected";
}

/** How a restore may proceed for a FRESH row:
 *   "standard" - status is deleted/paused
 *   "legacy"   - the old flow's stripped write (rejected, not approved, status text not
 *                paused/deleted) AND (the entry came from this browser's local snapshot OR the
 *                row carries an audit value)
 *   null       - refuse (a plain rejected applicant must never be approved by Restore) */
export function restoreMode(row, { fromSnapshot } = {}) {
  if (String((row && row.role) || "").toLowerCase() === "admin") return null;   // never restore-approve an admin row
  const s = statusOf(row);
  if (s === "deleted" || s === "paused") return "standard";
  if (isLegacyStrippedRow(row) && (fromSnapshot || hasAuditValue(row))) return "legacy";
  return null;
}

/** A restore is only legal for a deleted/paused row, or a legacy stripped row with proof. */
export function canRestoreRow(row, opts) {
  return restoreMode(row, opts) !== null;
}

/** Clear message for a refused restore. */
export function restoreBlockMessage(row) {
  if (row && row.rejected === true && row.approved !== true) {
    return "This account is rejected, not deleted or paused, and nothing in the database or this browser shows an admin paused or deleted it. " +
      "Restore is refused so a rejected applicant is never approved by accident. Use Approve on the Accounts tab if you really want to accept them.";
  }
  return "This account is not deleted or paused in the database (status: " + (statusOf(row) || "none") + "). Reload the page to see its current state.";
}

export const isRepAcct = a => !!a && (a.role === "rep" || a.tier === "rep");
const isInactive = a => !!a && (a.status === "deleted" || a.status === "paused");
const repNameOf = a => String(a.contact || a.name || "").trim().toLowerCase();

/** Reps (deleted or paused) drawn from the Supabase-hydrated accounts. */
export function inactiveRepAccounts(accounts) {
  return (accounts || []).filter(a => isRepAcct(a) && isInactive(a));
}

/** Lower-cased emails of inactive reps, used to hide demo seed copies. */
export function inactiveRepEmailSet(accounts) {
  const s = new Set();
  inactiveRepAccounts(accounts).forEach(a => { if (a.email && a.email !== "—") s.add(String(a.email).toLowerCase()); });
  return s;
}

/** Names (lower-cased, trimmed) of inactive reps. A name that ALSO belongs to an active
 *  (approved) rep is left out so an active namesake is never mislabelled or hidden. */
export function inactiveRepNameSet(accounts) {
  const active = new Set();
  (accounts || []).forEach(a => {
    if (isRepAcct(a) && a.status === "approved") { const n = repNameOf(a); if (n) active.add(n); }
  });
  const s = new Set();
  inactiveRepAccounts(accounts).forEach(a => {
    const n = repNameOf(a);
    if (n && n !== "—" && !active.has(n)) s.add(n);
  });
  return s;
}

/** Map of lower-cased inactive rep name -> "deleted" | "paused" (active namesakes excluded). */
export function inactiveRepStatusMap(accounts) {
  const names = inactiveRepNameSet(accounts);
  const m = new Map();
  inactiveRepAccounts(accounts).forEach(a => {
    const n = repNameOf(a);
    if (names.has(n)) m.set(n, a.status);
  });
  return m;
}

const isLiveAcct = a => !!a && (a.isLive === true || !!a.supabaseId);

/** Reps that must NOT be offered in any rep picker: deleted / paused reps, plus any rep whose LIVE
 *  Supabase profile is present and not approved (rejected, pending). The latter catches a rep
 *  deleted under the OLD flow whose status text was stripped and who is now just "rejected" in the
 *  database. Only accounts that came from a loaded profile row (isLive / supabaseId) count, so a
 *  viewer that cannot read other profiles simply gets an empty set (behaves as before). */
export function pickerBlockedRepAccounts(accounts) {
  return (accounts || []).filter(a => isRepAcct(a) && (isInactive(a) || (isLiveAcct(a) && a.status !== "approved")));
}

/** Lower-cased emails of picker-blocked reps. */
export function pickerBlockedRepEmailSet(accounts) {
  const s = new Set();
  pickerBlockedRepAccounts(accounts).forEach(a => { if (a.email && a.email !== "—") s.add(String(a.email).toLowerCase()); });
  return s;
}

/** Lower-cased names of picker-blocked reps; a name that ALSO belongs to an approved rep is left
 *  out so an active namesake is never hidden. */
export function pickerBlockedRepNameSet(accounts) {
  const active = new Set();
  (accounts || []).forEach(a => {
    if (isRepAcct(a) && a.status === "approved") { const n = repNameOf(a); if (n) active.add(n); }
  });
  const s = new Set();
  pickerBlockedRepAccounts(accounts).forEach(a => {
    const n = repNameOf(a);
    if (n && n !== "—" && !active.has(n)) s.add(n);
  });
  return s;
}

/** Display-only: dealer is assigned to a rep that is deleted/paused. `inactiveNames` is a Set
 *  or a Map (from inactiveRepStatusMap); anything with .has works. */
export function dealerHasInactiveRep(dealer, inactiveNames) {
  const r = String((dealer && dealer.rep) || "").trim().toLowerCase();
  return !!r && !!inactiveNames && inactiveNames.has(r);
}

/** "deleted" | "paused" | null for the badge on a dealer whose rep is inactive. */
export function dealerInactiveRepStatus(dealer, statusMap) {
  const r = String((dealer && dealer.rep) || "").trim().toLowerCase();
  return (r && statusMap && statusMap.get(r)) || null;
}

function byNewest(a, b) {
  const ta = new Date(a.deleted_at || a.paused_at || 0).getTime();
  const tb = new Date(b.deleted_at || b.paused_at || 0).getTime();
  return tb - ta;
}

/** Archive rows: every deleted/paused account (reps AND dealers), newest first. */
export function buildArchive(accounts) {
  const seen = new Set();
  return (accounts || []).filter(a => {
    if (!isInactive(a) || seen.has(a.id)) return false;
    seen.add(a.id);
    return true;
  }).sort(byNewest);
}

/** Merge per-browser snapshot entries (mpcare_rep_overrides, already shaped like accounts) into
 *  the Supabase-derived archive, deduped by id and email, regardless of whether the Supabase
 *  hydrate succeeded. Supabase wins: a snapshot entry whose live account is approved or pending
 *  in the database (e.g. restored on another device) is dropped; one whose live row is rejected
 *  (legacy stripped write) or that has no live row at all is kept. Snapshot entries are flagged
 *  fromSnapshot so Restore may accept the legacy stripped shape for them. */
export function mergeArchiveWithSnapshot(archive, snapshotEntries, accounts) {
  const byId = new Map();
  const byEmail = new Map();
  (accounts || []).forEach(a => {
    if (a && a.id != null) byId.set(String(a.id), a);
    if (a && a.email && a.email !== "—") byEmail.set(String(a.email).toLowerCase(), a);
  });
  const seenId = new Set();
  const seenEmail = new Set();
  const out = [];
  // Supabase-sourced archive rows are deduped by id ONLY (profiles.email is a copy and may have
  // drifted, so two distinct rows must never collapse). Snapshot entries are also deduped by email.
  const take = (a, byEmailToo) => {
    const id = String(a.id);
    const em = a.email && a.email !== "—" ? String(a.email).toLowerCase() : "";
    if (seenId.has(id) || (byEmailToo && em && seenEmail.has(em))) return;
    seenId.add(id);
    if (em) seenEmail.add(em);
    out.push(a);
  };
  (archive || []).forEach(a => take(a, false));
  (snapshotEntries || []).forEach(e => {
    if (!e) return;
    const live = byId.get(String(e.id)) || byId.get(String(e.supabaseId || "")) ||
      (e.email ? byEmail.get(String(e.email).toLowerCase()) : null);
    if (live && (live.status === "approved" || live.status === "pending")) return;
    take({ ...e, fromSnapshot: true }, true);
  });
  return out.sort(byNewest);
}

/** True when a rep NAME matches any rep-shaped account or picker entry (with or without a
 *  Supabase id). Used to decide whether an unresolvable previous rep deserves a visible warning
 *  (rep-shaped but unresolvable) or only a console warning (free text / demo name). */
export function repNameKnown(name, repList, accounts) {
  const n = String(name || "").trim().toLowerCase();
  if (!n) return false;
  if ((repList || []).some(r => String(r.name || "").trim().toLowerCase() === n)) return true;
  return (accounts || []).some(a => isRepAcct(a) &&
    [a.contact, a.name, a.email].some(v => String(v || "").trim().toLowerCase() === n));
}

/** Resolve a rep NAME to a Supabase rep id. Looks in the active picker list first, then in the
 *  FULL accounts list (which still contains deleted / paused reps) by contact / name / email.
 *  Returns null when it cannot be resolved (callers must surface that, never skip silently). */
export function resolveRepIdByName(name, repList, accounts) {
  const n = String(name || "").trim().toLowerCase();
  if (!n) return null;
  const hit = (repList || []).find(r => String(r.name || "").trim().toLowerCase() === n);
  if (hit && (hit.supabaseId || hit.id)) return hit.supabaseId || hit.id;
  const acct = (accounts || []).find(a => isRepAcct(a) && a.supabaseId &&
    [a.contact, a.name, a.email].some(v => String(v || "").trim().toLowerCase() === n));
  return acct ? acct.supabaseId : null;
}
